import { ComponentProps, useEffect, useMemo } from 'react';
import { BookOpen, Loader2, Trash2 } from 'lucide-react';

import { NavSection } from '@/components/nav-section';
import { NavUser } from '@/components/nav-user';
import { TeamSwitcher } from '@/components/team-switcher';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarRail } from '@/components/ui/sidebar';
import { INTEGRATIONS, TEAMS } from '@/lib/constants';
import { useUser } from '@/queries/useUser';
import { NavIntegrations } from './nav-integrations';
import { useConversations } from '@/queries/useConversations';
import { useConversationStore } from '@/stores/conversationStore';
import { MenuGroupItems, NavSectionItems } from '@/types';
import { deleteConversation } from '@/lib/api/conversations';

export function AppSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
    const {
        conversations: queryConversations,
        isFirstLoad: isLoadingConversations,
        refetchConversations,
    } = useConversations();
    const { conversations: storeConversations, setConversations } = useConversationStore();
    const { user } = useUser();

    // Sync React Query conversations to store (when query data changes)
    useEffect(() => {
        if (queryConversations) {
            setConversations(queryConversations);
        }
    }, [queryConversations, setConversations]);

    const navMain = useMemo<NavSectionItems>(() => {
        return (
            storeConversations?.map(conversation => ({
                title: conversation.isPending ? (
                    <span className='flex items-center space-x-2'>
                        <Loader2 className='h-3 w-3 animate-spin' />
                        <span className='text-muted-foreground'>...</span>
                    </span>
                ) : (
                    conversation.title
                ),
                url: `/chat?conversationId=${conversation.id}`,
                id: conversation.id,
            })) || []
        );
    }, [storeConversations]);

    const navIntegrations = useMemo(() => {
        return INTEGRATIONS.map(item => {
            return {
                ...item,
                url: `/integrations?integrationType=${item.type}`,
                isActive: user?.integrations?.[item.type]?.status === 'active',
            };
        });
    }, [user]);

    const conversationMenuItems: MenuGroupItems = [
        {
            title: 'Delete Chat',
            id: 'delete-conversation',
            icon: Trash2,
            action: id => {
                deleteConversation(id);
                refetchConversations();
            },
        },
    ];

    return (
        <Sidebar collapsible='icon' {...props}>
            <SidebarHeader>
                <TeamSwitcher teams={TEAMS} />
            </SidebarHeader>
            <SidebarContent>
                <NavIntegrations integrations={navIntegrations} />
                {!isLoadingConversations && (
                    <NavSection
                        header={{ title: 'Conversations', icon: BookOpen }}
                        items={navMain}
                        menuGroupItems={conversationMenuItems}
                    />
                )}
            </SidebarContent>
            <SidebarFooter>{user && <NavUser user={user} />}</SidebarFooter>
            <SidebarRail />
        </Sidebar>
    );
}
